import { motion } from 'framer-motion'
import { type ReactElement, useRef } from 'react'
import * as Accordion from '@radix-ui/react-accordion'
import * as Tabs from '@radix-ui/react-tabs'
import { ProductPage } from '@velasca/ui/product-page'
import { SerifTitle } from '@velasca/ui/serif-title'
import {
  AnimatedBorder,
  StorytellingItemTrigger,
  StorytellingItemWrapper,
  StorytellingSection,
  TabList,
} from './ProductStorytellingSection.styles'

const ACCORDION_ITEMS_ANIMATION_TIMING = 200

interface ProductStorytellingSectionProps {
  isDesktop: boolean
  desktopSelectedSection: number
  groupedSections: {
    id: string
    cta: string
    headerText: string
    component: ReactElement
  }[]
  setDesktopSelectedSection: (section: number) => void
  promotionalBannerHeight: number
  navbarHeight: number
  tabsRef: React.RefObject<HTMLUListElement>
}

export const ProductStorytellingSection = ({
  isDesktop,
  desktopSelectedSection,
  groupedSections,
  setDesktopSelectedSection,
  promotionalBannerHeight,
  navbarHeight,
  tabsRef,
}: ProductStorytellingSectionProps) => {
  const accordionItemRefs = useRef<Record<string, HTMLElement | null>>({})

  const handleAccordionValueChange = (value: string) => {
    if (!value || isDesktop) {
      return
    }

    // Small delay to sync with the accordion items animation
    setTimeout(() => {
      const accordionItem = accordionItemRefs.current[value]

      if (accordionItem) {
        const rect = accordionItem.getBoundingClientRect()
        const headerOffset = promotionalBannerHeight + navbarHeight

        const isHeaderNotFullyVisible = rect.top < headerOffset

        if (isHeaderNotFullyVisible) {
          const scrollTarget = window.scrollY + rect.top - headerOffset

          window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth',
          })
        }
      }
    }, ACCORDION_ITEMS_ANIMATION_TIMING)
  }

  return (
    <StorytellingSection id="product-storytelling-section">
      {isDesktop && (
        <Tabs.Root
          className="tablist-container"
          orientation="vertical"
          value={groupedSections[desktopSelectedSection]?.id}
          onValueChange={(value) => {
            const index = groupedSections.findIndex((s) => s.id === value)

            if (index !== -1) {
              setDesktopSelectedSection(index)
            }
          }}
        >
          <nav aria-label="Section navigation">
            <Tabs.List asChild>
              <TabList ref={tabsRef}>
                {groupedSections.map((section) => {
                  const isActive =
                    section.id === groupedSections[desktopSelectedSection]?.id

                  return (
                    <StorytellingItemWrapper key={section.id}>
                      {isActive && (
                        <AnimatedBorder
                          layoutId="storytelling-border"
                          initial={{ scaleY: 1 }}
                          animate={{ scaleY: [1, 1.6, 1] }}
                          transition={{
                            layout: {
                              type: 'spring',
                              mass: 1.4,
                              stiffness: 300,
                              damping: 35,
                            },
                            scaleY: {
                              duration: 0.5,
                              times: [0, 0.25, 1],
                              ease: [0.4, 0, 0.2, 1],
                            },
                          }}
                        />
                      )}
                      <Tabs.Trigger asChild value={section.id}>
                        <StorytellingItemTrigger isActive={isActive}>
                          {section.cta}
                        </StorytellingItemTrigger>
                      </Tabs.Trigger>
                    </StorytellingItemWrapper>
                  )
                })}
              </TabList>
            </Tabs.List>
          </nav>
          <div className="tablist-content">
            {groupedSections.map((section) => (
              <Tabs.Content asChild key={section.id} value={section.id}>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {section.component}
                </motion.div>
              </Tabs.Content>
            ))}
          </div>
        </Tabs.Root>
      )}
      {!isDesktop && (
        <Accordion.Root
          collapsible
          asChild
          type="single"
          defaultValue={groupedSections[0].id}
          onValueChange={handleAccordionValueChange}
        >
          <ProductPage.MobileAccordionRoot>
            {groupedSections.map((section) => (
              <Accordion.Item asChild key={section.id} value={section.id}>
                <ProductPage.MobileAccordionItem
                  ref={(el) => {
                    accordionItemRefs.current[section.id] = el
                  }}
                >
                  <Accordion.Header asChild>
                    <SerifTitle as="h2">
                      <ProductPage.MobileAccordionTrigger>
                        {section.cta}
                        <ProductPage.MobileAccordionChevron
                          $animationTiming={ACCORDION_ITEMS_ANIMATION_TIMING}
                        />
                      </ProductPage.MobileAccordionTrigger>
                    </SerifTitle>
                  </Accordion.Header>
                  <ProductPage.MobileAccordionContent
                    $animationTiming={ACCORDION_ITEMS_ANIMATION_TIMING}
                  >
                    {section.component}
                  </ProductPage.MobileAccordionContent>
                </ProductPage.MobileAccordionItem>
              </Accordion.Item>
            ))}
          </ProductPage.MobileAccordionRoot>
        </Accordion.Root>
      )}
    </StorytellingSection>
  )
}
